import { useEffect, useState } from 'react';

import { getDailyWeatherForecast } from '../../../api/rest/getDailyWeatherForecast';
import { ICoordinates } from '@app-types/geo';
import { IWeatherDaily } from '@domain/weather/types';
import { convertTemperature } from '@lib/units/convertTemperature';
import { getRelativeWeekDay } from '@lib/date/getRelativeWeekDay';
import { reportMykissClientError } from '@lib/errors/client';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { CurrentUser, useUserInfo } from '@domain/user/useUserInfo';

interface Result {
  weatherData?: IWeatherDailyData[];
  response: 'success' | 'fail';
}

export interface IWeatherDailyData {
  airTempMin: string;
  airTempMax: string;
  conditionCode: number;
  conditionIsNight: boolean;
  reading_at: string;
  dayOfWeek?: string;
}

function convertWeatherResponse(
  { readings }: IWeatherDaily,
  user: CurrentUser,
): IWeatherDailyData[] {
  return readings.map(reading => ({
    airTempMin: convertTemperature(reading.temperature_min, user?.temperatureUnit, { round: true }),
    airTempMax: convertTemperature(reading.temperature_max, user?.temperatureUnit, { round: true }),
    conditionCode: reading.wwo_condition.weather_code,
    conditionIsNight: reading.wwo_condition.night,
    reading_at: reading.reading_at,
    dayOfWeek: getRelativeWeekDay(reading.reading_at), // e.g. 'Saturday'
  }));
}

export function useDailyWeatherForecast(
  coordinates?: ICoordinates,
  daysAhead = 0,
  startToday = true,
): Result | undefined {
  const { user } = useUserInfo();
  const [state, setState] = useState<{
    weatherData?: IWeatherDailyData[];
    response: 'success' | 'fail';
  }>();
  const clientHasRendered = useClientHasRendered();

  useEffect(() => {
    const controller = new AbortController();

    if (clientHasRendered && coordinates) {
      getDailyWeatherForecast(coordinates, daysAhead, startToday)
        .then(response => {
          if (!controller.signal.aborted) {
            setState({ weatherData: convertWeatherResponse(response, user), response: 'success' });
          }
        })
        .catch((error: unknown) => {
          reportMykissClientError(error);
          if (!controller.signal.aborted) {
            setState({ response: 'fail' });
          }
        });
    }

    return () => {
      controller.abort();
    };
  }, [clientHasRendered, coordinates, daysAhead, startToday, user]);

  return state;
}
